import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { describeNodeType, type JourneyGraph, type JourneyNode } from '@mailflow/core/segments';
import { api } from '../lib/api.js';
import { listsQuery } from '../lib/contacts.js';
import { JourneyCanvas } from '../components/JourneyCanvas.js';
import { JourneySimulation } from '../components/JourneySimulation.js';

interface NodeStat {
  node_id: string;
  entered: number;
  yes_count: number;
  no_count: number;
}

interface JourneyDetail {
  journey: {
    id: string;
    name: string;
    description: string | null;
    status: string;
    version: number;
    graph: JourneyGraph;
  };
  nodeStats: NodeStat[];
  runCounts: Record<string, number>;
}

const RUN_LABELS: Record<string, string> = {
  active: 'unterwegs',
  waiting: 'wartend',
  completed: 'abgeschlossen',
  exited: 'ausgestiegen',
  failed: 'fehlgeschlagen',
};

export function JourneyDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const queryClient = useQueryClient();
  const [showEnroll, setShowEnroll] = useState(false);

  const detail = useQuery({
    queryKey: ['journeys', id],
    queryFn: ({ signal }) => api.get<JourneyDetail>(`/api/journeys/${id}`, signal),
    refetchInterval: (query) => (query.state.data?.journey.status === 'active' ? 10_000 : false),
  });

  const action = useMutation({
    mutationFn: (what: 'activate' | 'pause') => api.post(`/api/journeys/${id}/${what}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['journeys'] }),
  });

  if (detail.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!detail.data) return null;

  const { journey, nodeStats, runCounts } = detail.data;
  const statsByNode = Object.fromEntries(nodeStats.map((s) => [s.node_id, s]));

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <nav className="breadcrumb text-sm">
        <Link to="/journeys">Journeys</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">{journey.name}</span>
      </nav>

      <header className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h1 style={{ fontSize: 'var(--text-2xl)' }}>{journey.name}</h1>
            <span
              className="badge"
              data-status={journey.status === 'active' ? 'active' : 'paused'}
            >
              {journey.status === 'active' ? '🟢 Aktiv' : '⏸ Pausiert'}
            </span>
          </div>
          <p className="muted text-sm">
            Fassung {journey.version} · {journey.description ?? 'ohne Beschreibung'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button variant="secondary" onClick={() => setShowEnroll(true)}>
            👥 Kontakte aufnehmen
          </Button>
          {journey.status !== 'active' ? (
            <Button onClick={() => action.mutate('activate')} loading={action.isPending}>
              Scharf schalten
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => action.mutate('pause')}>
              Pausieren
            </Button>
          )}
        </div>
      </header>

      {action.isError && (
        <div className="alert" data-tone="danger" role="alert">
          {action.error instanceof Error ? action.error.message : 'Aktion fehlgeschlagen.'}
        </div>
      )}

      <div className="stat-grid">
        {Object.entries(RUN_LABELS).map(([key, label]) => (
          <div className="stat" key={key}>
            <span className="stat__label">{label}</span>
            <span className="stat__value" data-numeric>
              {(runCounts[key] ?? 0).toLocaleString('de-CH')}
            </span>
          </div>
        ))}
      </div>

      <section className="card">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Ablauf</h2>
          {/*
            Der Aufbau wird berechnet, nicht gespeichert: eine Ebene je
            Abstand vom Auslöser. Damit kann kein Ablauf kaputt aussehen,
            weil jemand einen Knoten verschoben hat — und die Zahlen an den
            Kästen sind gezählte Durchläufe, keine Attrappe.
          */}
          <JourneyCanvas
            graph={journey.graph}
            stats={Object.fromEntries(
              nodeStats.map((s) => [
                s.node_id,
                { entered: s.entered, yes_count: s.yes_count, no_count: s.no_count },
              ]),
            )}
          />
        </div>
      </section>

      <JourneySimulation journeyId={id} />

      <section className="card">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Schritte im Einzelnen</h2>
          <ol className="flow">
            {journey.graph.nodes.map((node) => (
              <FlowNode
                key={node.id}
                node={node}
                graph={journey.graph}
                stat={statsByNode[node.id]}
              />
            ))}
          </ol>
        </div>
      </section>

      {showEnroll && (
        <EnrollJourneyModal
          journeyId={id}
          journeyName={journey.name}
          onClose={() => setShowEnroll(false)}
          onSuccess={() => void detail.refetch()}
        />
      )}
    </div>
  );
}

function EnrollJourneyModal({
  journeyId,
  journeyName,
  onClose,
  onSuccess,
}: {
  journeyId: string;
  journeyName: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [sourceType, setSourceType] = useState<'list' | 'all'>('list');
  const [selectedListId, setSelectedListId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lists = useQuery(listsQuery);

  const handleEnroll = async () => {
    try {
      setLoading(true);
      setError(null);
      let targetContactIds: string[] = [];

      if (sourceType === 'list' && selectedListId) {
        const res = await api.get<{ items: Array<{ id: string }> }>(
          `/api/contacts?listId=${selectedListId}&limit=5000`,
        );
        targetContactIds = res.items.map((c) => c.id);
      } else {
        const res = await api.get<{ items: Array<{ id: string }> }>(
          `/api/contacts?status=active&limit=5000`,
        );
        targetContactIds = res.items.map((c) => c.id);
      }

      if (targetContactIds.length === 0) {
        setError('Keine aktiven Kontakte in der ausgewählten Quelle gefunden.');
        setLoading(false);
        return;
      }

      const res = await api.post<{ enrolled: number; skipped: number }>(
        `/api/journeys/${journeyId}/enroll`,
        { contactIds: targetContactIds },
      );

      alert(
        `${res.enrolled} Kontakte wurden erfolgreich in die Journey aufgenommen (${res.skipped} bereits verarbeitet/übersprungen).`,
      );
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aufnahme fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
    >
      <div
        className="card stack"
        style={{ width: '100%', maxWidth: '500px', padding: '24px', background: 'var(--color-surface, #fff)' }}
      >
        <h2 style={{ fontSize: '1.25rem' }}>👥 Kontakte in Journey aufnehmen</h2>
        <p className="muted text-sm">
          Wähle eine Empfängerliste oder erstelle einen Sofort-Durchlauf für den Ablauf „{journeyName}“.
        </p>

        {error && (
          <div className="alert" data-tone="danger" role="alert">
            {error}
          </div>
        )}

        <div className="stack" style={{ gap: '12px', margin: '12px 0' }}>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="source"
              checked={sourceType === 'list'}
              onChange={() => setSourceType('list')}
            />
            <strong>Aus einer spezifischen Liste aufnehmen</strong>
          </label>

          {sourceType === 'list' && (
            <select
              className="input"
              value={selectedListId}
              onChange={(e) => setSelectedListId(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">-- Bitte Liste auswählen --</option>
              {(lists.data?.items ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.contactCount} Kontakte)
                </option>
              ))}
            </select>
          )}

          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="source"
              checked={sourceType === 'all'}
              onChange={() => setSourceType('all')}
            />
            <strong>Alle aktiven Kontakte im Projekt aufnehmen</strong>
          </label>
        </div>

        <div className="cluster" style={{ justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void handleEnroll()}
            loading={loading}
            disabled={sourceType === 'list' && !selectedListId}
          >
            🚀 Kontakte aufnehmen
          </Button>
        </div>
      </div>
    </div>
  );
}

function FlowNode({
  node,
  graph,
  stat,
}: {
  node: JourneyNode;
  graph: JourneyGraph;
  stat?: NodeStat;
}) {
  const outgoing = graph.edges.filter((e) => e.from === node.id);

  return (
    <li className="flow__node" data-type={node.type}>
      <div className="flow__head">
        <strong>{node.label ?? describeNodeType(node)}</strong>
        {stat && (
          <span className="badge" data-numeric>
            {stat.entered} durchlaufen
          </span>
        )}
      </div>
      <span className="muted text-sm">{describeNodeType(node)}</span>

      {outgoing.length > 0 && (
        <ul className="flow__branches">
          {outgoing.map((edge) => (
            <li key={`${edge.branch}-${edge.to}`} data-branch={edge.branch}>
              {edge.branch === 'yes'
                ? 'ja'
                : edge.branch === 'no'
                  ? 'nein'
                  : edge.branch === 'timeout'
                    ? 'Zeit abgelaufen'
                    : 'weiter'}
              {' → '}
              <code>{edge.to}</code>
              {stat && edge.branch === 'yes' && <span className="muted"> ({stat.yes_count})</span>}
              {stat && edge.branch === 'no' && <span className="muted"> ({stat.no_count})</span>}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
